import { useMemo } from 'react';
import { GROUP_POSITION_VALUES } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';
import { calculateGroupPositions } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/calculateGroupPositions';

interface UseSeatDataParams {
    tableId: string | undefined;
    seatNumber: number;
}

const useSeatDetails = ({ tableId, seatNumber }: UseSeatDataParams) => {
    const { tickets, ticketGroups } = useTableSeatingStore(state => ({
        tickets: state.tickets,
        ticketGroups: state.ticketGroups,
    }));

    const tableTickets = useMemo(() => {
        if (!tableId || !tickets) return [];
        return Object.values(tickets)
            .filter(t => t.seating_group_id === tableId)
            .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));
    }, [tickets, tableId]);

    const ticket = tableTickets.find(t => t.seat_number === seatNumber);
    const ticketGroup = ticket?.ticket_group_id ? ticketGroups[ticket.ticket_group_id] : undefined;

    const ticketsWithPositions = useMemo(() => {
        if (!tableId || tableTickets.length === 0) return [];
        return calculateGroupPositions(tableTickets);
    }, [tableId, tableTickets]);

    return useMemo(() => {
        const seatData = ticketsWithPositions.find(item => item.ticket.seat_number === seatNumber);
        if (!seatData || !ticket) {
            return null;
        }

        const { groupPosition } = seatData;
        const isFirstInGroup =
            groupPosition === GROUP_POSITION_VALUES.FIRST ||
            groupPosition === GROUP_POSITION_VALUES.SOLO;
        const isLastInGroup =
            groupPosition === GROUP_POSITION_VALUES.LAST ||
            groupPosition === GROUP_POSITION_VALUES.SOLO;

        return {
            ticket,
            guestName: `${ticket.first_name} ${ticket.last_name}`,
            ticketGroup,
            groupPosition,
            hasGroup: !!ticket.ticket_group_id,
            isFirstInGroup,
            isLastInGroup,
            ticketId: ticket.id,
            ticketGroupId: ticket.ticket_group_id,
        };
    }, [seatNumber, ticketsWithPositions, ticket, ticketGroup]);
};

export { useSeatDetails };
